import { useEffect, useMemo, useState } from "react";
import { collection, doc, onSnapshot, serverTimestamp, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { Ban, CalendarClock, Gift, Play, ShieldCheck, Users } from "lucide-react";
import { db, functions } from "./firebase";

interface AccessRecord {
  uid: string;
  email: string;
  displayName: string;
  providers: string[];
  status: string;
  plan?: string;
  productId?: string;
  trialEndsAt?: { toDate?: () => Date };
  complimentaryEndsAt?: { toDate?: () => Date };
  currentPeriodEndsAt?: { toDate?: () => Date };
}

interface CreditRecord {
  uid: string;
  allowance: number;
  used: number;
  available: number;
  periodKey: string;
}

export function UserAccessAdmin() {
  const [access, setAccess] = useState<AccessRecord[]>([]);
  const [credits, setCredits] = useState<CreditRecord[]>([]);
  const [message, setMessage] = useState("");
  const [working, setWorking] = useState("");

  useEffect(() => onSnapshot(collection(db, "userAccess"), (snapshot) => {
    setAccess(snapshot.docs.map((item) => ({ uid: item.id, ...item.data() } as AccessRecord)));
  }, (reason) => setMessage(reason.message)), []);
  useEffect(() => onSnapshot(collection(db, "creditAccounts"), (snapshot) => {
    setCredits(snapshot.docs.map((item) => ({ uid: item.id, ...item.data() } as CreditRecord)));
  }, (reason) => setMessage(reason.message)), []);

  const rows = useMemo(() => access
    .map((record) => ({ ...record, credits: credits.find((item) => item.uid === record.uid) }))
    .sort((a, b) => a.email.localeCompare(b.email)), [access, credits]);

  const grant = async (record: AccessRecord) => {
    try {
      setWorking(record.uid);
      setMessage("");
      await httpsCallable(functions, "grantComplimentaryAccess")({ uid: record.uid, days: 30 });
      setMessage(`Granted 30 complimentary days to ${record.email || record.displayName || record.uid}.`);
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "Unable to grant complimentary access.");
    } finally {
      setWorking("");
    }
  };

  const suspend = async (record: AccessRecord) => {
    if (!window.confirm(`Suspend ${record.email || record.displayName || record.uid}? Their data will remain stored.`)) return;
    try {
      setWorking(record.uid);
      await updateDoc(doc(db, "userAccess", record.uid), {
        status: "suspended",
        suspendedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "Unable to suspend this account.");
    } finally {
      setWorking("");
    }
  };

  const startLaunchGrant = async () => {
    if (!window.confirm("Start the one-time 30-day complimentary period for every legacy approved tester? This cannot be restarted.")) return;
    try {
      setWorking("launch");
      const result = await httpsCallable(functions, "startTesterLaunchGrant")({});
      const data = result.data as { migratedCount?: number };
      setMessage(`Commercial launch grant started for ${data.migratedCount ?? 0} existing testers.`);
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "Unable to start the tester launch grant.");
    } finally {
      setWorking("");
    }
  };

  return <section className="access-admin">
    <header>
      <div><h2>Users &amp; Billing</h2><p>Monitor self-service trials, subscriptions, complimentary access, and AI credits.</p></div>
      <span><ShieldCheck size={15} />Owner only</span>
    </header>
    <div className="billing-admin-actions">
      <button disabled={Boolean(working)} onClick={() => void startLaunchGrant()}><Play size={14} />Start tester launch grant</button>
      <small>Use this once on commercial launch day. Existing approved testers remain unchanged until then.</small>
    </div>
    {message && <div className="notice">{message}</div>}
    <div className="access-table">
      <div className="access-head billing"><span>User</span><span>Entitlement</span><span>Credits</span><span>Renewal / expiry</span><span>Actions</span></div>
      {rows.map((record) => <div className="access-row billing" key={record.uid}>
        <span><strong>{record.displayName || record.email || "Unnamed user"}</strong><small>{record.email || record.uid}</small><small>{(record.providers || []).map(providerLabel).join(" · ") || "Provider unavailable"}</small></span>
        <span className={`access-status ${record.status}`}>{record.status}<small>{record.plan || "legacy"}</small></span>
        <span className="credit-cell"><strong>{formatCredits(record.credits?.available)}</strong><small>{record.credits?.allowance === -1 ? "Unlimited" : `${record.credits?.used ?? 0} used · ${record.credits?.periodKey ?? "not started"}`}</small></span>
        <span className="expiry-cell"><CalendarClock size={13} /><small>{formatExpiry(record)}</small></span>
        <span className="access-actions">
          {record.status !== "owner" && <button className="approve" disabled={working === record.uid} onClick={() => void grant(record)}><Gift size={13} />30 days</button>}
          {!["owner", "suspended"].includes(record.status) && <button disabled={working === record.uid} onClick={() => void suspend(record)}><Ban size={13} />Suspend</button>}
        </span>
      </div>)}
      {!rows.length && <div className="center-state"><Users size={24} /><span>No signed-in accounts yet.</span></div>}
    </div>
  </section>;
}

function providerLabel(value: string) {
  return value === "google.com" ? "Google" : value === "apple.com" ? "Apple" : value;
}

function formatCredits(value?: number) {
  return value === -1 ? "Unlimited" : `${Math.max(0, value ?? 0).toLocaleString()} left`;
}

function formatExpiry(record: AccessRecord) {
  const timestamp = record.trialEndsAt || record.complimentaryEndsAt || record.currentPeriodEndsAt;
  return timestamp?.toDate?.().toLocaleDateString() || "No expiry recorded";
}
